/**
 * SetcardView — responsive HTML-Ansicht einer PB-Setcard.
 *
 * Shared component, genutzt von PublicSetcardPage (öffentlich) und
 * PbSidebar-Modal (Admin). Rendert alle non-null Felder der SetcardData
 * in einer strukturierten, mobilfreundlichen Ansicht.
 *
 * Design: inline-styles, BRAND-Token, kein Tailwind/CSS-Modul.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import type { SetcardData } from '../../../lib/api';

interface Props {
  data: SetcardData;
  dark: boolean;
}

/** Render a simple label + value row, skip if value is null/empty. */
function FieldRow({ label, value, dark }: { label: string; value: string | number | null | undefined; dark: boolean }) {
  const tc = getThemeColors(dark);
  if (value == null || value === '') return null;
  return (
    <div style={{
      display: 'flex', gap: 8, padding: '7px 0',
      borderBottom: `1px solid ${tc.borderSoft}`,
      fontSize: 13,
    }}>
      <span style={{ color: tc.muted, fontWeight: 500, minWidth: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ color: tc.text, fontWeight: 400, flex: 1 }}>{String(value)}</span>
    </div>
  );
}

/** Section heading */
function SectionTitle({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const tc = getThemeColors(dark);
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: tc.muted,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      padding: '14px 0 6px',
      borderBottom: `2px solid ${tc.border}`,
      marginBottom: 2,
    }}>
      {children}
    </div>
  );
}

/** Star rating display */
function Stars({ value }: { value: number }) {
  const { t: tr } = useTranslation();
  const full = Math.round(value);
  return (
    <span aria-label={tr('pflege.setcard.stars_aria', { value })}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} style={{ color: i < full ? BRAND : '#d0d5dd', fontSize: 14 }}>★</span>
      ))}
    </span>
  );
}

export function SetcardView({ data, dark }: Props) {
  const { t: tr } = useTranslation();
  const tc = getThemeColors(dark);

  const fullName = [data.anrede, data.vorname, data.nachname].filter(Boolean).join(' ');

  return (
    <div style={{
      fontFamily: 'Outfit, sans-serif',
      color: tc.text,
      maxWidth: '100%',
    }}>
      {/* ── Header: Name + Pflegegrad-Badge ─────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
        marginBottom: 20,
      }}>
        {/* Photo */}
        {data.photo_base64 && (
          <img
            src={data.photo_base64}
            alt={tr('pflege.setcard.photo_alt', { name: fullName })}
            style={{
              width: 100, height: 100,
              objectFit: 'cover', borderRadius: 12,
              flexShrink: 0, border: `2px solid ${tc.border}`,
            }}
          />
        )}
        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 160 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: tc.heading, lineHeight: 1.2 }}>
            {fullName}
          </h2>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {data.pflegegrad && (
              <span style={{
                background: dark ? '#2a1222' : BRAND_LIGHT,
                color: dark ? '#f49dd1' : BRAND_DARK,
                borderRadius: 999, padding: '3px 11px', fontSize: 12, fontWeight: 700,
              }}>
                {data.pflegegrad}
              </span>
            )}
            <span style={{
              background: tc.chipBg, color: tc.muted,
              borderRadius: 999, padding: '3px 11px', fontSize: 12, fontWeight: 600,
            }}>
              {data.alter} {tr('pflege.detail.years')}
            </span>
            <span style={{
              background: tc.chipBg, color: tc.muted,
              borderRadius: 999, padding: '3px 11px', fontSize: 12, fontWeight: 600,
            }}>
              {data.geschlecht}
            </span>
          </div>
        </div>
      </div>

      {/* ── Stammdaten ───────────────────────────────────────────────── */}
      <SectionTitle dark={dark}>{tr('pflege.form.section.stammdaten.title')}</SectionTitle>
      <FieldRow dark={dark} label={tr('pflege.form.field.groesse')} value={data.groesse != null ? `${data.groesse} cm` : null} />
      <FieldRow dark={dark} label={tr('pflege.form.field.gewicht')} value={data.gewicht != null ? `${data.gewicht} kg` : null} />
      <FieldRow dark={dark} label={tr('pflege.form.field.familienstand')} value={data.familienstand} />
      <FieldRow dark={dark} label={tr('pflege.setcard.field.nettolohn')} value={data.nettolohn ? `${data.nettolohn} €` : null} />
      <FieldRow dark={dark} label={tr('pflege.form.field.wohnort')} value={data.wohnort} />

      {/* ── Gesundheit & Pflege ──────────────────────────────────────── */}
      {(data.beweglichkeit || data.einschraenkungen || data.sonstige_einschraenkungen ||
        data.besserung || data.sinneswahrnehmung || data.geisteszustand ||
        data.schlafverhalten || data.grundpflege || data.hygiene ||
        data.urinkontrolle || data.stuhlkontrolle || data.ankleiden || data.essen) && (
        <>
          <SectionTitle dark={dark}>{tr('pflege.setcard.section.gesundheit_pflege')}</SectionTitle>
          <FieldRow dark={dark} label={tr('pflege.form.field.beweglichkeit')} value={data.beweglichkeit} />
          <FieldRow dark={dark} label={tr('pflege.form.field.einschraenkungen')} value={data.einschraenkungen} />
          <FieldRow dark={dark} label={tr('pflege.form.field.sonstige_einschraenkungen')} value={data.sonstige_einschraenkungen} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.besserungsaussichten')} value={data.besserung} />
          <FieldRow dark={dark} label={tr('pflege.form.field.sinneswahrnehmung')} value={data.sinneswahrnehmung} />
          <FieldRow dark={dark} label={tr('pflege.form.field.geisteszustand')} value={data.geisteszustand} />
          <FieldRow dark={dark} label={tr('pflege.form.field.schlafverhalten')} value={data.schlafverhalten} />
          <FieldRow dark={dark} label={tr('pflege.form.field.grundpflege')} value={data.grundpflege} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.hygiene')} value={data.hygiene} />
          <FieldRow dark={dark} label={tr('pflege.form.field.urinkontrolle')} value={data.urinkontrolle} />
          <FieldRow dark={dark} label={tr('pflege.form.field.stuhlkontrolle')} value={data.stuhlkontrolle} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.ankleiden')} value={data.ankleiden} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.essen')} value={data.essen} />
          <FieldRow dark={dark} label={tr('pflege.form.field.charaktereigenschaften')} value={data.charaktereigenschaften} />
        </>
      )}

      {/* ── Haushalt & Umfeld ────────────────────────────────────────── */}
      {(data.bk_anrede || data.wohnsituation || data.wohnzustand || data.haustiere ||
        data.haustiere_welche || data.einkaufsmoeglichkeiten ||
        data.bk_wohnen || data.bk_aktivitaeten || data.bk_deutsch || data.bk_fuehrerschein) && (
        <>
          <SectionTitle dark={dark}>{tr('pflege.setcard.section.haushalt_umfeld')}</SectionTitle>
          <FieldRow dark={dark} label={tr('pflege.setcard.field.anrede_bk')} value={data.bk_anrede} />
          <FieldRow dark={dark} label={tr('pflege.form.field.wohnsituation')} value={data.wohnsituation} />
          <FieldRow dark={dark} label={tr('pflege.familienkonto.detail.wohnen.wohnzustand')} value={data.wohnzustand} />
          <FieldRow dark={dark} label={tr('pflege.form.field.haustiere')} value={data.haustiere} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.haustiere_welche')} value={data.haustiere_welche} />
          <FieldRow dark={dark} label={tr('pflege.form.field.einkaufsmoeglichkeiten')} value={data.einkaufsmoeglichkeiten} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.bk_wohnen')} value={data.bk_wohnen} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.bk_aktivitaeten')} value={data.bk_aktivitaeten} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.deutschkenntnisse_bk')} value={data.bk_deutsch} />
          <FieldRow dark={dark} label={tr('pflege.setcard.field.fuehrerschein_bk')} value={data.bk_fuehrerschein} />
        </>
      )}

      {/* ── Freitext-Felder ──────────────────────────────────────────── */}
      {(data.hobbys || data.umfeld || data.sonstiges) && (
        <>
          <SectionTitle dark={dark}>{tr('pflege.setcard.section.weiteres')}</SectionTitle>
          {data.hobbys && (
            <div style={{ padding: '10px 0', borderBottom: `1px solid ${tc.borderSoft}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{tr('pflege.setcard.field.hobbys_interessen')}</div>
              <div style={{ fontSize: 13, color: tc.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.hobbys}</div>
            </div>
          )}
          {data.umfeld && (
            <div style={{ padding: '10px 0', borderBottom: `1px solid ${tc.borderSoft}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{tr('pflege.setcard.field.umfeld')}</div>
              <div style={{ fontSize: 13, color: tc.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.umfeld}</div>
            </div>
          )}
          {data.sonstiges && (
            <div style={{ padding: '10px 0', borderBottom: `1px solid ${tc.borderSoft}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{tr('pflege.form.field.sonstiges')}</div>
              <div style={{ fontSize: 13, color: tc.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.sonstiges}</div>
            </div>
          )}
        </>
      )}

      {/* ── Bewertungen ─────────────────────────────────────────────── */}
      {data.ratings && data.ratings.length > 0 && (
        <>
          <SectionTitle dark={dark}>{tr('pflege.detail.tabs.ratings')}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
            {data.ratings.map((r, i) => (
              <div key={i} style={{
                background: tc.cardMuted, borderRadius: 10,
                padding: '12px 14px', border: `1px solid ${tc.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: tc.heading }}>{r.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Stars value={r.rating_gesamt} />
                    <span style={{ fontSize: 11, color: tc.muted }}>{r.date}</span>
                  </div>
                </div>
                {r.rating_text && (
                  <p style={{ margin: 0, fontSize: 13, color: tc.text, lineHeight: 1.5 }}>{r.rating_text}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
